// pages/MyBookings.jsx — Ink & Lime Design System
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import bookingService from '../../services/bookingService';
import { ROUTES, STORAGE_KEYS } from '../../utils/constants';
import TripManagementModal from '../../components/rental/TripManagementModal';

const BASEURL = import.meta.env.VITE_BASE_URL || '';

// ── Design Tokens ─────────────────────────────────────────────────────────
const T = {
  ink:     '#0D0D0F',
  ink2:    '#1A1A20',
  slate:   '#2D2D38',
  muted:   '#6B6B7A',
  line:    '#E8E8F0',
  surface: '#F5F5FA',
  white:   '#FFFFFF',
  lime:    '#C8F23C',
  teal:    '#00BFA5',
  coral:   '#FF6D3A',
  amber:   '#F59E0B',
};

// ── Status config ─────────────────────────────────────────────────────────
const STATUS_MAP = {
  confirmed:          { label: 'Confirmed',   bg: '#1e40af', color: '#fff',        dot: '#60a5fa' },
  accepted:           { label: 'Accepted',    bg: '#00BFA5', color: '#fff',        dot: T.teal },
  'booking accepted': { label: 'Accepted',    bg: '#00BFA5', color: '#fff',        dot: T.teal },
  started:            { label: 'Trip Active', bg: T.lime,    color: T.ink,         dot: T.ink, pulse: true },
  'trip started':     { label: 'Trip Active', bg: T.lime,    color: T.ink,         dot: T.ink, pulse: true },
  'start trip':       { label: 'Trip Active', bg: T.lime,    color: T.ink,         dot: T.ink, pulse: true },
  'trip extend':      { label: 'Extended',    bg: T.amber,   color: '#fff',        dot: T.amber },
  ended:              { label: 'Ended',       bg: T.coral,   color: '#fff',        dot: T.coral },
  'trip ended':       { label: 'Ended',       bg: T.coral,   color: '#fff',        dot: T.coral },
  'end trip':         { label: 'Ended',       bg: T.coral,   color: '#fff',        dot: T.coral },
  completed:          { label: 'Completed',   bg: T.teal,    color: '#fff',        dot: T.teal },
  cancelled:          { label: 'Cancelled',   bg: '#ef4444', color: '#fff',        dot: '#ef4444' },
};

const getStatus = (status) => STATUS_MAP[status?.toLowerCase()] || { label: status || 'Unknown', bg: T.slate, color: '#fff', dot: T.muted };

// ── Modern Toast Alert ────────────────────────────────────────────────────
const Toast = ({ show, onClose, type, title, message, autoClose = true }) => {
  useEffect(() => {
    if (show && autoClose) {
      const t = setTimeout(onClose, 4200);
      return () => clearTimeout(t);
    }
  }, [show, onClose, autoClose]);

  if (!show) return null;

  const cfg = {
    success: { icon: '✓', accent: T.lime,  bg: T.ink },
    error:   { icon: '✕', accent: '#ef4444', bg: T.ink },
    info:    { icon: 'i', accent: '#60a5fa', bg: T.ink },
  }[type] || { icon: 'i', accent: T.lime, bg: T.ink };

  return (
    <div style={{
      position: 'fixed', top: 24, right: 24, zIndex: 9999,
      maxWidth: 360, width: 'calc(100vw - 48px)',
      animation: 'toastIn .35s cubic-bezier(.16,1,.3,1)',
    }}>
      <style>{`@keyframes toastIn{from{opacity:0;transform:translateX(100%) scale(.9);}to{opacity:1;transform:none;}}`}</style>
      <div style={{
        background: T.ink, borderRadius: 16,
        border: `1.5px solid ${cfg.accent}`,
        boxShadow: `0 20px 60px rgba(0,0,0,.5), 0 0 0 1px rgba(0,0,0,.2)`,
        overflow: 'hidden',
      }}>
        {/* Top accent bar */}
        <div style={{ height: 2, background: cfg.accent }} />
        <div style={{ padding: '14px 16px', display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          {/* Icon */}
          <div style={{
            width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
            background: `${cfg.accent}20`, border: `2px solid ${cfg.accent}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 14, fontWeight: 900, color: cfg.accent,
          }}>
            {cfg.icon}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ fontSize: 13, fontWeight: 800, color: T.white, marginBottom: 3 }}>{title}</p>
            <p style={{ fontSize: 12, color: 'rgba(255,255,255,.55)', lineHeight: 1.5 }}>{message}</p>
            {autoClose && (
              <div style={{ marginTop: 10, height: 2, background: 'rgba(255,255,255,.1)', borderRadius: 2, overflow: 'hidden' }}>
                <div style={{ height: '100%', background: cfg.accent, borderRadius: 2, animation: 'shrinkBar 4.2s linear forwards' }} />
                <style>{`@keyframes shrinkBar{from{width:100%;}to{width:0;}}`}</style>
              </div>
            )}
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: T.muted, cursor: 'pointer', fontSize: 16, padding: 2, flexShrink: 0 }}>✕</button>
        </div>
      </div>
    </div>
  );
};

// ── Success Overlay ───────────────────────────────────────────────────────
const SuccessOverlay = ({ show, onClose, title, message }) => {
  useEffect(() => {
    if (show) { const t = setTimeout(onClose, 3200); return () => clearTimeout(t); }
  }, [show, onClose]);

  if (!show) return null;
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 300,
      background: 'rgba(13,13,15,.85)', backdropFilter: 'blur(10px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
      animation: 'fadeIn .3s ease',
    }}>
      <style>{`@keyframes fadeIn{from{opacity:0;}to{opacity:1;}} @keyframes popIn{from{opacity:0;transform:scale(.6);}to{opacity:1;transform:scale(1);}}`}</style>
      <div style={{
        background: T.white, borderRadius: 24, padding: '40px 36px', textAlign: 'center',
        maxWidth: 380, width: '100%',
        border: `2px solid ${T.lime}`,
        boxShadow: '0 40px 100px rgba(0,0,0,.4)',
        animation: 'popIn .4s cubic-bezier(.16,1,.3,1)',
      }}>
        <div style={{
          width: 72, height: 72, borderRadius: '50%',
          background: T.ink, border: `3px solid ${T.lime}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 20px',
          fontSize: 28, color: T.lime,
        }}>✓</div>
        <h2 style={{ fontSize: 20, fontWeight: 900, color: T.ink, marginBottom: 8 }}>{title}</h2>
        <p style={{ fontSize: 14, color: T.muted, lineHeight: 1.6, marginBottom: 24 }}>{message}</p>
        <button onClick={onClose} style={{
          padding: '11px 28px', borderRadius: 12, background: T.ink, color: T.lime,
          border: 'none', fontSize: 13, fontWeight: 800, cursor: 'pointer',
        }}>Continue</button>
      </div>
    </div>
  );
};

// ── Document Card ─────────────────────────────────────────────────────────
const DocCard = ({ title, icon, imageUrl, status }) => {
  const [err, setErr] = useState(false);
  return (
    <div style={{ background: T.surface, borderRadius: 14, padding: 16, border: `1.5px solid ${T.line}` }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 20 }}>{icon}</span>
          <h3 style={{ fontSize: 14, fontWeight: 800, color: T.ink }}>{title}</h3>
        </div>
        {status !== undefined && (
          <div style={{ padding: '3px 10px', borderRadius: 20, background: status ? 'rgba(0,191,165,.12)' : 'rgba(239,68,68,.12)', border: `1px solid ${status ? T.teal : '#ef4444'}`, fontSize: 10, fontWeight: 800, color: status ? T.teal : '#ef4444' }}>
            {status ? '✓ Valid' : '✗ Invalid'}
          </div>
        )}
      </div>
      <div style={{ borderRadius: 10, overflow: 'hidden', background: T.line, marginBottom: 8 }}>
        {err ? (
          <div style={{ height: 160, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: T.muted, fontSize: 12 }}>
            <span style={{ fontSize: 32, marginBottom: 8 }}>📄</span> Image not available
          </div>
        ) : (
          <img src={imageUrl} alt={title} onError={() => setErr(true)}
            onClick={() => !err && window.open(imageUrl, '_blank')}
            style={{ width: '100%', maxHeight: 200, objectFit: 'contain', cursor: 'pointer', display: 'block' }}
          />
        )}
      </div>
      <button onClick={() => !err && window.open(imageUrl, '_blank')} disabled={err}
        style={{ width: '100%', padding: '8px', borderRadius: 8, border: 'none', background: err ? T.line : T.ink, color: err ? T.muted : T.lime, fontSize: 12, fontWeight: 700, cursor: err ? 'not-allowed' : 'pointer' }}>
        {err ? '❌ Unavailable' : '🔍 View Full Size'}
      </button>
    </div>
  );
};

// ── Documents Modal ───────────────────────────────────────────────────────
const DocsModal = ({ show, onClose, documents, loading }) => {
  if (!show) return null;
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, background: 'rgba(13,13,15,.85)', backdropFilter: 'blur(8px)' }}>
      <div style={{
        background: T.white, borderRadius: 20, width: '100%', maxWidth: 640,
        maxHeight: '90vh', display: 'flex', flexDirection: 'column',
        border: `1.5px solid ${T.line}`, boxShadow: '0 40px 100px rgba(0,0,0,.4)',
        overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: `1px solid ${T.line}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: T.ink }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: T.lime, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>📄</div>
            <div>
              <h2 style={{ fontSize: 16, fontWeight: 800, color: T.white }}>Bike Documents</h2>
              {documents?.registrationNumber && <p style={{ fontSize: 11, color: T.muted }}>{documents.registrationNumber}</p>}
            </div>
          </div>
          <button onClick={onClose} style={{ width: 32, height: 32, borderRadius: '50%', border: `1px solid ${T.slate}`, background: 'none', color: T.muted, cursor: 'pointer', fontSize: 16 }}>✕</button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
          {loading ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: 48 }}>
              <div style={{ width: 36, height: 36, borderRadius: '50%', border: `3px solid ${T.lime}`, borderTopColor: 'transparent', animation: 'spin .8s linear infinite', marginBottom: 14 }} />
              <p style={{ fontSize: 13, color: T.muted }}>Loading documents…</p>
              <style>{`@keyframes spin{to{transform:rotate(360deg);}}`}</style>
            </div>
          ) : documents ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {documents.registrationCertificate && <DocCard title="Registration Certificate" icon="📄" imageUrl={documents.registrationCertificate} status={documents.isDocuments} />}
              {documents.insuranceCertificate    && <DocCard title="Insurance Certificate"    icon="🛡️" imageUrl={documents.insuranceCertificate}    status={documents.isInsurance} />}
              {documents.pollutionCertificate    && <DocCard title="Pollution Certificate"    icon="🌱" imageUrl={documents.pollutionCertificate}    status={documents.isPuc} />}

              {!documents.registrationCertificate && !documents.insuranceCertificate && !documents.pollutionCertificate && (
                <div style={{ textAlign: 'center', padding: 48 }}>
                  <div style={{ fontSize: 40, marginBottom: 12 }}>📭</div>
                  <p style={{ color: T.muted, fontSize: 14 }}>No documents available for this bike</p>
                </div>
              )}

              {(documents.chassisNumber || documents.engineNumber) && (
                <div style={{ padding: '14px 16px', borderRadius: 12, background: T.surface, border: `1px solid ${T.line}` }}>
                  <h3 style={{ fontSize: 12, fontWeight: 800, color: T.muted, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 10 }}>🔧 Vehicle Info</h3>
                  {documents.chassisNumber && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: `1px solid ${T.line}`, fontSize: 13 }}>
                      <span style={{ color: T.muted }}>Chassis Number</span>
                      <span style={{ fontWeight: 700, color: T.ink, fontFamily: 'monospace' }}>{documents.chassisNumber}</span>
                    </div>
                  )}
                  {documents.engineNumber && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', fontSize: 13 }}>
                      <span style={{ color: T.muted }}>Engine Number</span>
                      <span style={{ fontWeight: 700, color: T.ink, fontFamily: 'monospace' }}>{documents.engineNumber}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: 48 }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>⚠️</div>
              <p style={{ color: T.muted, fontSize: 14 }}>Failed to load documents</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 20px', borderTop: `1px solid ${T.line}`, background: T.surface }}>
          <button onClick={onClose} style={{ width: '100%', padding: '12px', borderRadius: 12, background: T.ink, color: T.lime, border: 'none', fontSize: 13, fontWeight: 800, cursor: 'pointer' }}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Pagination ────────────────────────────────────────────────────────────
const Pagination = ({ currentPage, totalPages, pageSize, onPageChange, onPageSizeChange }) => {
  const pages = [];
  const max = 5;
  let start = Math.max(1, currentPage - Math.floor(max / 2));
  let end = Math.min(totalPages, start + max - 1);
  if (end - start + 1 < max) start = Math.max(1, end - max + 1);
  for (let i = start; i <= end; i++) pages.push(i);

  return (
    <div style={{ background: T.white, borderRadius: 16, border: `1.5px solid ${T.line}`, padding: '14px 20px', display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 12, color: T.muted }}>Show</span>
        <select value={pageSize} onChange={e => onPageSizeChange(+e.target.value)}
          style={{ padding: '6px 10px', borderRadius: 8, border: `1.5px solid ${T.line}`, fontSize: 12, color: T.ink, background: T.surface, outline: 'none' }}>
          {[5, 10, 20, 50].map(n => <option key={n} value={n}>{n}</option>)}
        </select>
        <span style={{ fontSize: 12, color: T.muted }}>per page</span>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
        <button onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1}
          style={{ width: 34, height: 34, borderRadius: 8, border: `1.5px solid ${T.line}`, background: T.white, cursor: currentPage === 1 ? 'not-allowed' : 'pointer', opacity: currentPage === 1 ? .35 : 1, fontSize: 16, color: T.muted }}>
          ‹
        </button>
        {start > 1 && <>
          <button onClick={() => onPageChange(1)} style={pgBtn(1 === currentPage)}>1</button>
          {start > 2 && <span style={{ color: T.muted, fontSize: 12 }}>…</span>}
        </>}
        {pages.map(p => <button key={p} onClick={() => onPageChange(p)} style={pgBtn(p === currentPage)}>{p}</button>)}
        {end < totalPages && <>
          {end < totalPages - 1 && <span style={{ color: T.muted, fontSize: 12 }}>…</span>}
          <button onClick={() => onPageChange(totalPages)} style={pgBtn(totalPages === currentPage)}>{totalPages}</button>
        </>}
        <button onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages}
          style={{ width: 34, height: 34, borderRadius: 8, border: `1.5px solid ${T.line}`, background: T.white, cursor: currentPage === totalPages ? 'not-allowed' : 'pointer', opacity: currentPage === totalPages ? .35 : 1, fontSize: 16, color: T.muted }}>
          ›
        </button>
      </div>

      <span style={{ fontSize: 12, color: T.muted }}>Page {currentPage} of {totalPages}</span>
    </div>
  );
};

const pgBtn = (active) => ({
  width: 34, height: 34, borderRadius: 8, border: `1.5px solid ${active ? T.ink : T.line}`,
  background: active ? T.ink : T.white, color: active ? T.lime : T.slate,
  fontSize: 12, fontWeight: active ? 800 : 600, cursor: 'pointer',
  transition: 'all .15s', transform: active ? 'scale(1.06)' : 'none',
});

// ── Booking Card ──────────────────────────────────────────────────────────
const BookingCard = ({ booking, onCancel, onStartTrip, onEndTrip, onViewDocs, onInvoice }) => {
  const [hov, setHov] = useState(false);
  const s = getStatus(booking.status);
  const status = booking.status?.toLowerCase();

  const canCancel     = ['confirmed', 'accepted', 'booking accepted'].includes(status) && !['started','trip started','start trip'].includes(status);
  const canStart      = ['accepted','booking accepted'].includes(status);
  const canEnd        = ['started','trip started','start trip','trip extend'].includes(status);
  const canDocs       = ['started','trip started','start trip','trip extend','ended','trip ended','end trip'].includes(status);
  const waitInvoice   = ['ended','trip ended','end trip'].includes(status);
  const showInvoice   = status === 'completed';

  const fmtDate = (d) => { try { return new Date(d).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}); } catch { return 'N/A'; }};
  const bikeName = booking.bikeDetails?.registrationNumber || `Bike #${booking.vehicleId}`;
  const custName = booking.customerName || `Customer #${booking.customerId}`;

  return (
    <article onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{
        background: T.white, borderRadius: 18, overflow: 'hidden',
        border: `1.5px solid ${hov ? T.lime : T.line}`,
        transition: 'all .22s', transform: hov ? 'translateY(-4px)' : 'none',
        boxShadow: hov ? '0 18px 48px rgba(0,0,0,.09)' : '0 2px 8px rgba(0,0,0,.04)',
        display: 'flex', flexDirection: 'column',
      }}>

      {/* Top strip — status color */}
      <div style={{ height: 3, background: s.bg }} />

      <div style={{ padding: '16px 18px', flex: 1, display: 'flex', flexDirection: 'column', gap: 12 }}>

        {/* Header row */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
          <div>
            <p style={{ fontSize: 10, fontWeight: 700, color: T.muted, letterSpacing: .8, marginBottom: 2 }}>#{booking.bookingId}</p>
            <h3 style={{ fontSize: 16, fontWeight: 800, color: T.ink }}>🏍️ {bikeName}</h3>
            <p style={{ fontSize: 12, color: T.muted, marginTop: 2 }}>👤 {custName}</p>
          </div>
          {/* Status badge */}
          <div style={{
            padding: '4px 11px', borderRadius: 20, flexShrink: 0,
            background: s.bg, color: s.color,
            fontSize: 10, fontWeight: 800, letterSpacing: .5,
            display: 'flex', alignItems: 'center', gap: 5,
          }}>
            {s.pulse && <span style={{ width: 6, height: 6, borderRadius: '50%', background: s.dot, animation: 'pulse 1.2s infinite', display: 'inline-block' }} />}
            {s.label}
          </div>
        </div>

        {/* Details grid */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          {[
            { label: 'Start', value: fmtDate(booking.startDate1 || booking.startDate) },
            { label: 'End',   value: fmtDate(booking.endDate1 || booking.endDate) },
            { label: 'Amount', value: `₹${(booking.finalAmount||0).toLocaleString()}` },
            { label: 'Payment', value: booking.paymentStatus || 'PENDING' },
          ].map(({ label, value }) => (
            <div key={label} style={{ padding: '8px 10px', borderRadius: 10, background: T.surface, border: `1px solid ${T.line}` }}>
              <p style={{ fontSize: 9, fontWeight: 700, color: T.muted, textTransform: 'uppercase', letterSpacing: .8, marginBottom: 2 }}>{label}</p>
              <p style={{ fontSize: 13, fontWeight: 700, color: T.ink }}>{value}</p>
            </div>
          ))}
        </div>

        <p style={{ fontSize: 10, color: T.muted }}>Created: {fmtDate(booking.createdAt)}</p>

        {/* Action buttons */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 7, marginTop: 'auto' }}>
          {canStart && (
            <button onClick={() => onStartTrip(booking, 'start')}
              style={actionBtn(T.teal, '#fff')}>
              🚀 Start Trip
            </button>
          )}
          {canCancel && !canStart && (
            <button onClick={() => onCancel(booking.id)}
              style={actionBtn('#ef4444', '#fff')}>
              ✕ Cancel Booking
            </button>
          )}
          {canEnd && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 7 }}>
              <button onClick={() => onEndTrip(booking, 'end')} style={actionBtn(T.coral, '#fff')}>🏁 End Trip</button>
              {canDocs && <button onClick={() => onViewDocs(booking.vehicleId)} style={actionBtn(T.ink, T.lime)}>📄 Docs</button>}
            </div>
          )}
          {waitInvoice && (
            <button disabled style={{ ...actionBtn(T.amber, '#fff'), opacity: .8, cursor: 'not-allowed', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7 }}>
              <div style={{ width: 12, height: 12, borderRadius: '50%', border: '2px solid #fff', borderTopColor: 'transparent', animation: 'spin .8s linear infinite' }} />
              ⏳ Waiting for Invoice
            </button>
          )}
          {showInvoice && (
            <button onClick={() => onInvoice(booking)} style={actionBtn(T.ink, T.lime)}>📄 View Invoice</button>
          )}
        </div>
      </div>
    </article>
  );
};

const actionBtn = (bg, color) => ({
  width: '100%', padding: '10px', borderRadius: 10, border: 'none',
  background: bg, color, fontSize: 12, fontWeight: 800, cursor: 'pointer',
  transition: 'opacity .15s',
});

// ── Main Page ─────────────────────────────────────────────────────────────
const MyBookings = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [bookings,      setBookings]      = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState(null);
  const [alert,         setAlert]         = useState({ show: false });
  const [success,       setSuccess]       = useState({ show: false });

  const [currentPage,   setCurrentPage]   = useState(1);
  const [pageSize,      setPageSize]      = useState(10);
  const [totalPages,    setTotalPages]    = useState(1);
  const [sortBy,        setSortBy]        = useState('latest');
  const [activeTab,     setActiveTab]     = useState('all');

  const [tripModal,     setTripModal]     = useState({ show: false, booking: null, type: null });
  const [docsModal,     setDocsModal]     = useState({ show: false, data: null, loading: false });

  useEffect(() => {
    if (location.state?.showSuccess) {
      setSuccess({ show: true, title: '🎉 Booking Confirmed!', message: 'Your booking has been successfully created!' });
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  useEffect(() => { fetchBookings(); }, [currentPage, pageSize, sortBy, user]);

  const getCustomerId = () => {
    const u = user?.data || user;
    return u?.id || u?.userId || u?.customerId || u?.sub;
  };

  const getBikeName = (b) => b.bikeDetails?.registrationNumber || `Bike #${b.vehicleId}`;

  const notify = (type, title, message) => setAlert({ show: true, type, title, message });

  const fetchBookings = async () => {
    try {
      setLoading(true); setError(null);
      const cid = getCustomerId();
      if (!cid) { setError('Customer ID not found. Please login again.'); return; }

      const res = await bookingService.getBookingsByCustomer(cid, currentPage - 1, pageSize, sortBy);
      const list = Array.isArray(res) ? res : res.data || [];

      const sorted = [...list].sort((a, b) => {
        if (sortBy === 'latest') return b.id - a.id;
        if (sortBy === 'oldest') return a.id - b.id;
        if (sortBy === 'amount') return (b.finalAmount||0) - (a.finalAmount||0);
        return 0;
      });

      const latest = localStorage.getItem('latestBooking');
      if (latest) {
        try {
          const lb = JSON.parse(latest);
          if (!sorted.some(b => b.id === lb.id || b.bookingId === lb.bookingId)) sorted.unshift(lb);
          localStorage.removeItem('latestBooking');
        } catch { localStorage.removeItem('latestBooking'); }
      }

      setBookings(sorted);
      setTotalPages(Math.ceil(sorted.length / pageSize) || 1);
    } catch (e) {
      console.error(e);
      setError('Failed to load bookings. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async (bookingId) => {
    if (!window.confirm('Cancel this booking?')) return;
    try {
      await bookingService.cancelBooking(bookingId, 'USER');
      notify('success', 'Booking Cancelled', 'Your booking has been cancelled.');
      fetchBookings();
    } catch { notify('error', 'Error', 'Failed to cancel booking.'); }
  };

  // Uses axios (not fetch) so upload progress can be reported to the modal —
  // photo uploads were feeling "stuck" with no feedback during the network transfer.
  const tripApiCall = async (bookingId, formData, endpoint, onProgress) => {
    const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
    if (!token || token.length < 20) throw new Error('You are not logged in. Please login again.');
    try {
      const res = await axios.post(
        `${BASEURL}/api/booking-bikes/${bookingId}/${endpoint}`,
        formData,
        {
          headers: { Authorization: `Bearer ${token}` },
          onUploadProgress: (evt) => {
            if (onProgress && evt.total) onProgress(Math.round((evt.loaded / evt.total) * 100));
          },
        }
      );
      return res.data;
    } catch (err) {
      const data = err.response?.data;
      throw new Error(data?.message || data?.error || (err.response ? `HTTP ${err.response.status}` : err.message));
    }
  };

  const handleStartTrip = async (bookingId, formData, onProgress) => {
    await tripApiCall(bookingId, formData, 'start', onProgress);
    await fetchBookings();
    setTripModal({ show: false, booking: null, type: null });
    setSuccess({ show: true, title: 'Trip Started! 🚀', message: `Trip started for booking ${bookingId}.` });
  };

  const handleEndTrip = async (bookingId, formData, onProgress) => {
    await tripApiCall(bookingId, formData, 'end', onProgress);
    await fetchBookings();
    setTripModal({ show: false, booking: null, type: null });
    setSuccess({ show: true, title: 'Trip Ended! 🏁', message: `Trip ended for booking ${bookingId}.` });
  };

  const handleViewDocs = async (vehicleId) => {
    setDocsModal({ show: true, data: null, loading: true });
    try {
      const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
      if (!token || token.length < 20) throw new Error('Not logged in.');
      const res = await fetch(`${BASEURL}/api/bikes/view-documents/${vehicleId}`, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (!res.ok) throw new Error(`Status ${res.status}`);
      const d = await res.json();
      setDocsModal({
        show: true, loading: false,
        data: {
          registrationCertificate: d.documentImage || d.documentImageUrl,
          insuranceCertificate:    d.insuranceImage || d.insuranceImageUrl,
          pollutionCertificate:    d.pucImage       || d.pucImageUrl,
          isPuc: d.isPuc, isInsurance: d.isInsurance, isDocuments: d.isDocuments,
          registrationNumber: d.registrationNumber,
          chassisNumber: d.chassisNumber, engineNumber: d.engineNumber,
        },
      });
    } catch (e) {
      notify('error', 'Error', e.message || 'Failed to load documents.');
      setDocsModal({ show: false, data: null, loading: false });
    }
  };

  const filtered = bookings.filter(b => {
    const s = b.status?.toLowerCase();
    if (activeTab === 'active')    return !['completed','cancelled'].includes(s);
    if (activeTab === 'completed') return s === 'completed';
    return true;
  });

  const paginated = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const TABS = [
    { key: 'all',       label: 'All Bookings', count: bookings.length },
    { key: 'active',    label: 'Active',        count: bookings.filter(b => !['completed','cancelled'].includes(b.status?.toLowerCase())).length },
    { key: 'completed', label: 'Completed',     count: bookings.filter(b => b.status?.toLowerCase() === 'completed').length },
  ];

  // Loading
  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: T.surface, fontFamily: "'DM Sans',sans-serif" }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ width: 44, height: 44, borderRadius: '50%', border: `3px solid ${T.lime}`, borderTopColor: 'transparent', animation: 'spin .8s linear infinite', margin: '0 auto 14px' }} />
        <p style={{ fontSize: 14, fontWeight: 700, color: T.muted }}>Loading your bookings…</p>
        <style>{`@keyframes spin{to{transform:rotate(360deg);}}`}</style>
      </div>
    </div>
  );

  // Error
  if (error) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: T.surface, fontFamily: "'DM Sans',sans-serif" }}>
      <div style={{ textAlign: 'center', padding: '0 24px' }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>❌</div>
        <h3 style={{ fontSize: 18, fontWeight: 800, color: T.ink, marginBottom: 8 }}>Error Loading Bookings</h3>
        <p style={{ fontSize: 14, color: T.muted, marginBottom: 24 }}>{error}</p>
        <button onClick={fetchBookings} style={{ padding: '11px 28px', borderRadius: 12, background: T.ink, color: T.lime, border: 'none', fontSize: 13, fontWeight: 800, cursor: 'pointer' }}>
          Try Again →
        </button>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: T.surface, fontFamily: "'DM Sans',sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;700;800;900&family=Space+Grotesk:wght@700&display=swap');
        * { box-sizing: border-box; }
        @keyframes spin  { to { transform: rotate(360deg); } }
        @keyframes pulse { 0%,100%{opacity:1;} 50%{opacity:.35;} }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-thumb { background: ${T.line}; border-radius: 4px; }
      `}</style>

      <Toast {...alert} onClose={() => setAlert(a => ({ ...a, show: false }))} />
      <SuccessOverlay {...success} onClose={() => setSuccess(s => ({ ...s, show: false }))} />

      {/* ── HERO HEADER ── */}
      <div style={{ background: T.ink, position: 'relative', overflow: 'hidden', padding: '36px 24px 32px' }}>
        <div style={{ position: 'absolute', inset: 0, backgroundImage: `linear-gradient(${T.slate} 1px,transparent 1px),linear-gradient(90deg,${T.slate} 1px,transparent 1px)`, backgroundSize: '52px 52px', opacity: .2 }} />
        <div style={{ position: 'absolute', top: -80, right: -40, width: 300, height: 300, background: T.lime, borderRadius: '50%', filter: 'blur(90px)', opacity: .06 }} />

        <div style={{ maxWidth: 1200, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <button onClick={() => navigate(ROUTES.RENTAL)}
              style={{ width: 36, height: 36, borderRadius: 10, border: `1px solid ${T.slate}`, background: 'none', color: T.white, fontSize: 18, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              ←
            </button>
            <div>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(200,242,60,.1)', border: '1px solid rgba(200,242,60,.25)', borderRadius: 20, padding: '3px 10px', marginBottom: 4 }}>
                <span style={{ width: 5, height: 5, borderRadius: '50%', background: T.lime, display: 'block', animation: 'pulse 1.5s infinite' }} />
                <span style={{ fontSize: 9, fontWeight: 800, color: T.lime, letterSpacing: 1.5, textTransform: 'uppercase' }}>Ride History</span>
              </div>
              <h1 style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 'clamp(22px,4vw,34px)', fontWeight: 700, color: T.white }}>
                🏍️ My Bookings
              </h1>
              <p style={{ fontSize: 12, color: 'rgba(255,255,255,.4)', marginTop: 2 }}>Track & manage all your bike rentals</p>
            </div>
          </div>
          {/* Total count */}
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 38, fontWeight: 700, color: T.lime, lineHeight: 1 }}>{bookings.length}</div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,.35)', marginTop: 2 }}>Total Bookings</div>
          </div>
        </div>
      </div>

      {/* ── MAIN CONTENT ── */}
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '24px 20px 60px' }}>

        {/* Controls bar */}
        <div style={{ background: T.white, borderRadius: 16, border: `1.5px solid ${T.line}`, padding: '14px 18px', marginBottom: 20, display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>

          {/* Tabs */}
          <div style={{ display: 'flex', background: T.surface, borderRadius: 12, padding: 4, gap: 2 }}>
            {TABS.map(tab => (
              <button key={tab.key} onClick={() => { setActiveTab(tab.key); setCurrentPage(1); }}
                style={{
                  padding: '8px 16px', borderRadius: 9, border: 'none', cursor: 'pointer',
                  background: activeTab === tab.key ? T.ink : 'transparent',
                  color: activeTab === tab.key ? T.lime : T.muted,
                  fontSize: 12, fontWeight: 700, transition: 'all .15s',
                  display: 'flex', alignItems: 'center', gap: 6,
                }}>
                {tab.label}
                {tab.count > 0 && (
                  <span style={{ padding: '1px 7px', borderRadius: 10, background: activeTab === tab.key ? T.lime : T.line, color: activeTab === tab.key ? T.ink : T.muted, fontSize: 10, fontWeight: 800 }}>
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 12, color: T.muted }}>Sort:</span>
              <select value={sortBy} onChange={e => { setSortBy(e.target.value); setCurrentPage(1); }}
                style={{ padding: '7px 12px', borderRadius: 10, border: `1.5px solid ${T.line}`, fontSize: 12, color: T.ink, background: T.surface, outline: 'none' }}>
                <option value="latest">Latest First</option>
                <option value="oldest">Oldest First</option>
                <option value="amount">Amount: High to Low</option>
              </select>
            </div>

            <Link to={ROUTES.RENTAL}
              style={{ padding: '8px 18px', borderRadius: 10, background: T.ink, color: T.lime, fontSize: 12, fontWeight: 800, textDecoration: 'none' }}>
              + New Booking
            </Link>
          </div>
        </div>

        {/* Empty state */}
        {filtered.length === 0 ? (
          <div style={{ background: T.white, borderRadius: 20, padding: '60px 24px', textAlign: 'center', border: `1.5px solid ${T.line}` }}>
            <div style={{ fontSize: 52, marginBottom: 16 }}>📋</div>
            <h3 style={{ fontSize: 20, fontWeight: 900, color: T.ink, marginBottom: 8 }}>No Bookings Found</h3>
            <p style={{ fontSize: 14, color: T.muted, marginBottom: 24 }}>Ready to start your next bike journey?</p>
            <Link to={ROUTES.RENTAL}
              style={{ padding: '12px 28px', borderRadius: 12, background: T.ink, color: T.lime, fontSize: 13, fontWeight: 800, textDecoration: 'none' }}>
              🚀 Book a Bike
            </Link>
          </div>
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16, marginBottom: 20 }}>
              {paginated.map(b => (
                <BookingCard
                  key={b.id}
                  booking={b}
                  onCancel={handleCancel}
                  onStartTrip={(booking, type) => setTripModal({ show: true, booking, type })}
                  onEndTrip={(booking, type) => setTripModal({ show: true, booking, type })}
                  onViewDocs={handleViewDocs}
                  onInvoice={b => navigate(`/rental/invoice/${b.id}`, { state: { booking: b } })}
                />
              ))}
            </div>

            {totalPages > 1 && (
              <Pagination
                currentPage={currentPage} totalPages={totalPages} pageSize={pageSize}
                onPageChange={p => { setCurrentPage(p); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                onPageSizeChange={n => { setPageSize(n); setCurrentPage(1); }}
              />
            )}
          </>
        )}
      </div>

      {/* Trip Modal */}
      <TripManagementModal
        show={tripModal.show}
        onClose={() => setTripModal({ show: false, booking: null, type: null })}
        booking={tripModal.booking}
        onStartTrip={handleStartTrip}
        onEndTrip={handleEndTrip}
        tripType={tripModal.type}
      />

      {/* Docs Modal */}
      <DocsModal
        show={docsModal.show}
        onClose={() => setDocsModal({ show: false, data: null, loading: false })}
        documents={docsModal.data}
        loading={docsModal.loading}
      />
    </div>
  );
};

export default MyBookings;
